import { Fragment, type ReactElement } from 'react';
import { s, typographyTokens } from '@/theme/tokens';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { Box, Link, Text } from '@/ui/primitives';
import { tableV2NamePrimaryFontSize } from './TableV2Constants';
import { getTableV2LinkRoutingProps } from './TableV2LinkRouting';
import {
  tableV2NameCellContentSx,
  tableV2SingleLineNameStackSx,
  tableV2TwoLineNameStackSx,
} from './TableV2NameCellStyles';

export const TableV2NameCell = ({
  href,
  primary,
  secondary,
  secondaryHref,
  secondaryItems,
}: {
  href?: string | null;
  primary: string;
  secondary?: string | null;
  secondaryHref?: string | null;
  secondaryItems?: Array<{ href?: string | null; label: string }>;
}): ReactElement => {
  const hasSecondaryItems = secondaryItems !== undefined && secondaryItems.length > 0;
  const hasSecondary =
    hasSecondaryItems || (secondary !== null && secondary !== undefined && secondary.length > 0);
  const secondaryTitle = hasSecondaryItems
    ? secondaryItems.map((item) => item.label).join(' / ')
    : (secondary ?? '');

  return (
    <Box sx={tableV2NameCellContentSx}>
      <Box
        sx={[
          { display: 'grid', gap: s('none'), minWidth: 0, overflow: 'hidden' },
          hasSecondary ? tableV2TwoLineNameStackSx : tableV2SingleLineNameStackSx,
        ]}
      >
        <Text
          role="body"
          component="div"
          sx={{
            '&.MuiTypography-root': {
              fontSize: tableV2NamePrimaryFontSize,
            },
            fontSize: tableV2NamePrimaryFontSize,
            fontWeight: typographyTokens.bodyMedium.fontWeight,
            lineHeight: 1.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {href ? (
            <OverflowTooltip title={primary} inline>
              <Link
                {...getTableV2LinkRoutingProps(href)}
                sx={{
                  '&.MuiTypography-root': {
                    fontSize: tableV2NamePrimaryFontSize,
                  },
                  color: 'text.primary',
                  display: 'inline-block',
                  fontSize: tableV2NamePrimaryFontSize,
                  fontWeight: typographyTokens.bodyMedium.fontWeight,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textDecorationColor: 'currentColor',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {primary}
              </Link>
            </OverflowTooltip>
          ) : (
            primary
          )}
        </Text>
        {hasSecondary ? (
          <Text
            role="captionSmall"
            color="textSecondary"
            component="div"
            sx={{
              fontSize: (theme) => theme.typography.caption.fontSize,
              lineHeight: 1.25,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {hasSecondaryItems ? (
              <OverflowTooltip title={secondaryTitle} inline>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {secondaryItems.map((item, index) => (
                    <Fragment key={`${item.label}-${String(index)}`}>
                      {index > 0 ? ' / ' : null}
                      {item.href ? (
                        <Link
                          {...getTableV2LinkRoutingProps(item.href)}
                          sx={{
                            color: 'text.secondary',
                            textDecorationColor: 'currentColor',
                          }}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        item.label
                      )}
                    </Fragment>
                  ))}
                </Box>
              </OverflowTooltip>
            ) : secondaryHref ? (
              <OverflowTooltip title={secondaryTitle} inline>
                <Link
                  {...getTableV2LinkRoutingProps(secondaryHref)}
                  sx={{
                    color: 'text.secondary',
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textDecorationColor: 'currentColor',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {secondaryTitle}
                </Link>
              </OverflowTooltip>
            ) : (
              <OverflowTooltip title={secondaryTitle} inline>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {secondaryTitle}
                </Box>
              </OverflowTooltip>
            )}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
};
